/**
 * 📍 장소별 센 수 입력 — 10층 공장 · B1 창고 (v2.226.0 · 2026-10-02 · 사용자 요청)
 *
 * 분기 실사 카드 · 주간 확인 카드가 같이 쓴다. 장소 목록은 서버가 준다(StockLocation[]).
 * ⭐합계 줄은 **미리보기** — 맞음·다름은 저장 순간 서버가 합계와 장부로 판정한다.
 * ⭐빈 칸은 0 으로 본다(사용자 결정) — 그래서 합계 줄은 빈 칸을 0 으로 더한다.
 *   단, 「나중에」로 넘길 때는 빈 칸을 보내지 않는다(아직 안 센 장소 ≠ 0).
 * ⭐🧮 계산기는 **고른 칸**(테두리가 파란 칸)에 넣는다 — 칸을 누르면 그 칸이 골라진다.
 */

import type { LocDraft, StockLocation } from '../lib/stock-locations';
import { parseDraft } from '../lib/stock-locations';

interface Props {
  locations: readonly StockLocation[];
  draft: LocDraft;
  onChange: (next: LocDraft) => void;
  /** 🧮 계산기 답이 들어갈 칸 */
  active: string;
  onActive: (key: string) => void;
  /** 비교할 장부(현재고) */
  refQty: number;
  refLabel?: string;
  disabled?: boolean;
  /** 합계가 장부와 다를 때 덧붙이는 안내(예: 「아직 안 센 곳이 있으면 [⏳ 나중에]」) */
  diffHint?: string;
}

function fmt(n: number): string {
  return n.toLocaleString('ko-KR');
}

export function LocationQtyFields({ locations, draft, onChange, active, onActive, refQty, refLabel = '현재고', disabled, diffHint }: Props) {
  const p = parseDraft(draft, locations);
  const delta = p.sum - refQty;
  return (
    <div data-loc-fields>
      <div className={`grid gap-2 ${locations.length >= 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {locations.map((l) => {
          const on = active === l.key;
          return (
            <label key={l.key}
              className={`block rounded border-2 px-2 pt-1.5 pb-2 bg-white ${on ? 'border-blue-500' : 'border-slate-200'}`}>
              <span className="block text-xs font-semibold text-slate-600 text-center whitespace-nowrap">{l.label}</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                data-loc-input={l.key}
                value={draft[l.key] ?? ''}
                disabled={disabled}
                onFocus={() => onActive(l.key)}
                onChange={(e) => onChange({ ...draft, [l.key]: e.target.value.replace(/[^\d]/g, '').slice(0, 6) })}
                placeholder="0"
                aria-label={`${l.label} 센 수`}
                className="w-full mt-0.5 text-center text-2xl font-bold tabular-nums text-slate-900 bg-transparent focus:outline-none placeholder:text-slate-300 disabled:text-slate-400"
              />
            </label>
          );
        })}
      </div>
      <div data-loc-sum className={`mt-1.5 text-xs text-center ${
        p.bad ? 'text-red-600' : !p.any ? 'text-slate-500' : delta === 0 ? 'text-emerald-700 font-semibold' : 'text-amber-700 font-semibold'}`}>
        {p.bad
          ? `${p.bad} 칸은 0 이상 정수만 적을 수 있습니다`
          : !p.any
            ? '장소별로 센 수를 적어 주세요 (빈 칸은 0 으로 봅니다)'
            : delta === 0
              ? `합계 ${fmt(p.sum)} = ${refLabel} ${fmt(refQty)}`
              : `합계 ${fmt(p.sum)} · ${refLabel} ${fmt(refQty)} (${delta > 0 ? '+' : ''}${fmt(delta)})`}
      </div>
      {!p.bad && p.any && delta !== 0 && diffHint && (
        <div className="mt-0.5 text-xs text-center text-slate-500">{diffHint}</div>
      )}
    </div>
  );
}
